import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  ArrowRight,
} from "lucide-react";
import UserNavBar2 from "../UserNavBar2";
import Footer from "../Footer";
import ResourceCard from "../learner/ResourceCard";
import {useCart} from "../CardContext";
import CourseDiscovery from "./CourseDiscovery";
import {TrendingNow} from "./DiscoverySections";
import {useUserPublicCatalog} from "../../store/user";
import {
  mapPublicBook,
  mapPublicJournal,
  mapPublicResource,
  materialDetailPath,
} from "./publicMaterialMap";
import {
  PLATFORM_CARDS,
  LANDING_COURSES,
} from "./landingData";
import {useUserAuth} from "../../store/user";

// ── Shared section chrome ───────────────────────────────────────────────────

function SectionShell({eyebrow, title, description, action, actionTo, tone = "white", id, children}) {
  const navigate = useNavigate();
  return (
    <section
      id={id}
      className={`border-t border-slate-100 px-4 py-14 sm:py-16 ${tone === "slate" ? "bg-slate-50" : "bg-white"}`}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-2">
          <div className="max-w-2xl">
            {eyebrow ? (
              <span className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                {eyebrow}
              </span>
            ) : null}
            {title ? (
              <h2 className="mt-3 text-2xl tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
            ) : null}
            {description ? (
              <p className="mt-2 text-sm text-slate-600 sm:text-base">{description}</p>
            ) : null}
          </div>
          {action ? (
            <button
              type="button"
              onClick={() => navigate(actionTo || "/courses")}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-800"
            >
              {action} <ArrowRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {children}
      </div>
    </section>
  );
}

// ── Hero (light, four platform entry points) ────────────────────────────────

function PlatformHero({firstName}) {
  return (
    <section className="hero-section border-b border-slate-200 bg-slate-50 px-4 py-10 sm:py-12">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <h1 className="text-3xl  tracking-tight text-slate-900 sm:text-4xl">
            Welcome back{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Learn, practise and pass — explore our platforms, then dive into the courses below.
          </p>
        </div>

        {/* Four platform cards — one line on desktop */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PLATFORM_CARDS.map((card) => {
            return (
              <a
                key={card.id}
                href={card.href}
                target="_blank"
                rel="noreferrer"
                className="group relative flex flex-col overflow-hidden rounded-2xl p-3 text-white no-underline transition-all duration-300 ease-out hover:no-underline hover:-translate-y-1.5"
                style={{
                  background: `linear-gradient(160deg, ${card.accent[1]} 0%, ${card.accent[0]} 60%)`,
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -8px 14px -8px rgba(0,0,0,0.6), 0 14px 26px -12px ${card.accent[0]}, 0 3px 8px -3px rgba(0,0,0,0.25)`,
                }}>
                  
                {/* soft gloss for a modern 3D feel */}
                <span className="pointer-events-none absolute -left-8 -top-10 h-24 w-28 rounded-full bg-white/25 blur-2xl" />
                <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent" />

                <h2 className="relative text-sm font-extrabold leading-snug tracking-tight drop-shadow-sm">
                  {card.title}
                </h2>
                <span className="relative mt-1.5 block h-0.5 w-8 rounded-full bg-white/60" />
                <p className="relative mt-1.5 text-[14px] font-medium leading-snug text-white/90">
                  {card.description}
                </p>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function useMarketplaceSections() {
  const {
    fetchPublicBooks,
    fetchPublicJournals,
    fetchPublicResources,
  } = useUserPublicCatalog();
  const [sections, setSections] = useState({
    books: [], journals: [], projects: [], seminars: [],
    loading: true, errors: {},
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setSections((current) => ({...current, loading: true, errors: {}}));
      const [books, journals, projects, seminars] = await Promise.all([
        fetchPublicBooks({page: 1, limit: 4, sort: "newest"}),
        fetchPublicJournals({page: 1, limit: 3, sort: "newest"}),
        fetchPublicResources({page: 1, limit: 4, sort: "newest", type: "project-topics"}),
        fetchPublicResources({page: 1, limit: 4, sort: "newest", type: "seminar-topics"}),
      ]);
      if (!active) return;
      setSections({
        loading: false,
        books: (books.items || []).map(mapPublicBook),
        journals: (journals.items || []).map(mapPublicJournal),
        projects: (projects.items || []).map(mapPublicResource),
        seminars: (seminars.items || []).map(mapPublicResource),
        errors: {
          books: books.success ? "" : books.error,
          journals: journals.success ? "" : journals.error,
          projects: projects.success ? "" : projects.error,
          seminars: seminars.success ? "" : seminars.error,
        },
      });
    };
    load();
    return () => { active = false; };
  }, [fetchPublicBooks, fetchPublicJournals, fetchPublicResources]);

  return sections;
}

function MaterialCardGrid({items, loading, error, emptyMessage, columns = 4}) {
  const navigate = useNavigate();
  const {addToCart} = useCart();
  const columnClass = columns === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4";

  if (loading) {
    return <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${columnClass}`}>{Array.from({length: columns}, (_, index) => <div key={index} className="h-72 animate-pulse rounded-md border border-slate-200 bg-slate-100" />)}</div>;
  }
  if (error) return <p role="alert" className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</p>;
  if (!items.length) return <p className="py-8 text-center text-sm text-slate-500">{emptyMessage}</p>;

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${columnClass}`}>
      {items.map((item) => (
        <ResourceCard
          key={item.id}
          image={item.image}
          title={item.title}
          subtitle={item.author || item.subtitle}
          badge={item.badge}
          meta={item.meta}
          price={item.price}
          isFree={item.isFree}
          cornerClass="rounded-md"
          compact
          onOpen={() => navigate(materialDetailPath(item))}
          primaryLabel="View details"
          onPrimary={() => navigate(materialDetailPath(item))}
          secondaryLabel={item.isFree ? undefined : "Add to cart"}
          onSecondary={() => addToCart({
            id: item.id,
            type: item.type,
            title: item.title,
            instructor: item.author || item.subtitle || "",
            thumbnail: item.image,
            category: item.badge,
            price: item.price,
          })}
        />
      ))}
    </div>
  );
}

// ── Books & journals (reuse the learner ResourceCard) ───────────────────────

function BooksSection({items, loading, error}) {
  return (
    <SectionShell
      eyebrow="Books"
      title="Recommended books"
      description="Curated study material across core subjects — read online or download."
      action="Browse all books"
      actionTo="/books"
      tone="slate"
      id="books"
    >
      <MaterialCardGrid items={items} loading={loading} error={error} emptyMessage="No books are available right now." />
    </SectionShell>
  );
}

function JournalsSection({items, loading, error}) {
  return (
    <SectionShell
      eyebrow="Journals"
      title="Academic journals"
      description="Peer-reviewed journals and publications for research and reference."
      action="Browse all journals"
      actionTo="/journals"
      id="journals"
    >
      <MaterialCardGrid items={items} loading={loading} error={error} emptyMessage="No journals are available right now." columns={3} />
    </SectionShell>
  );
}

// ── Projects & seminars ─────────────────────────────────────────────────────

function ProjectsSection({items, loading, error}) {
  return (
    <SectionShell
      eyebrow="Projects"
      title="Project topics & research"
      description="Explore ready-made project topics with materials and expert guidance."
      id="projects"
    >
      <MaterialCardGrid items={items} loading={loading} error={error} emptyMessage="No project resources are available right now." />
    </SectionShell>
  );
}

function SeminarsSection({items, loading, error}) {
  return (
    <SectionShell
      eyebrow="Seminars"
      title="Seminars & presentations"
      description="Watch expert-led seminars across science, business, engineering and more."
      tone="slate"
      id="seminars"
    >
      <MaterialCardGrid items={items} loading={loading} error={error} emptyMessage="No seminar resources are available right now." />
    </SectionShell>
  );
}

// ── Root ────────────────────────────────────────────────────────────────────

const AuthenticatedLanding = () => {
  const {user} = useUserAuth();
  const materials = useMarketplaceSections();

  const name =
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    (typeof window !== "undefined" ? window.localStorage.getItem("name") : "") ||
    "";
  const firstName = String(name).trim().split(/\s+/)[0] || "";

  return (
    <div className="min-h-screen bg-white">
      <UserNavBar2 skipRemoteData />
      <main>
        <PlatformHero firstName={firstName} />

        {/* Course marketplace — same as the logged-out page, but showing every
            course so learners see the full catalogue at a glance. */}
        <CourseDiscovery pageSize={LANDING_COURSES.length} />

        <BooksSection items={materials.books} loading={materials.loading} error={materials.errors.books} />
        <TrendingNow />

        <ProjectsSection items={materials.projects} loading={materials.loading} error={materials.errors.projects} />
        <SeminarsSection items={materials.seminars} loading={materials.loading} error={materials.errors.seminars} />
        <JournalsSection items={materials.journals} loading={materials.loading} error={materials.errors.journals} />
      </main>
      <Footer />
    </div>
  );
};

export default AuthenticatedLanding;
